import { SurveyResultsFilterNames } from 'models/ISurveyResultsFilter';
import { IsoString } from 'models/IsoString';
import {
	ICalculate,
	ICalculated,
} from 'modules/admin-panel/pages/results/helpers/calculateSubmitted';
import { StatisticGroupingTypes } from 'modules/admin-panel/models/MapInfoModes';

export interface IBaseFilterParams {
	surveyId?: string;
	roundId?: string;
	serviceId?: number | string;
	roleId?: number;
	businessUnit?: string;
	region?: string;
	country?: string;
	city?: string;
	office?: string;
}

export interface IFilterDataPayload extends IBaseFilterParams {
	marks?: number[];
	examples?: string[];
	boundGetAllServiceData?: boolean;
	isServicePage?: boolean;
}

export interface IResultFilterParams extends IBaseFilterParams {
	surveyRoundId?: string;
}

export interface IStatisticParams extends IResultFilterParams {
	groupingType?: StatisticGroupingTypes;
	isServicePage?: boolean;
}

export type GroupingType = 'location' | 'role' | 'business_unit';
export interface IStatisticPayload extends IResultFilterParams {
	groupingType?: GroupingType;
	isServicePage?: boolean;
}

export interface IStatisticPayloadWithType extends IStatisticPayload {
	groupingType: GroupingType;
}

export interface IWordcloudParams extends IBaseFilterParams {
	surveyRoundId?: string;
	examples?: string[];
}

export interface IResultTrend extends ICalculate {
	endDate: IsoString;
	startDate: IsoString;
	avgMark: number | null;
	roundId: string;
	maxOfRange: number;
}

export interface IResultTrendCalculated extends IResultTrend, ICalculated {}

export interface ISecondaryTrendParams {
	serviceId?: number | string;
	roleId?: number;
	country?: string;
}

export interface IStatisticItem {
	name: string;
	npsPercent?: number;
	submittedCount: number;
	serviceStatistics: IServiceStatItem[];
	statistics: IStatisticItem[];
}

export interface IPerformanceSlidersNames {
	firstPerformanceSliderName: string;
	secondPerformanceSliderName: string;
	thirdPerformanceSliderName: string;
}

export interface IResultServiceParam {
	surveyRoundId: string;
}

export interface IResultParam {
	id: number;
	name: string;
}

export interface IResultRole extends IResultParam {}

export interface IResultService extends IResultRole {
	examples?: string[];
}

export enum ServiceTrend {
	up = 'UP',
	eq = 'EQ',
	down = 'DOWN',
}

export type IServiceTrend = ServiceTrend | null;

export interface IServiceStatItem extends IResultParam {
	satisfaction: number | null;
	previousSatisfaction: number | null;
	firstSatisfaction: number | null;
	trend: IServiceTrend;
	id: number;
}

export interface IMapPoint {
	country: string;
	city: string;
	office: string;
	latitude: number;
	longitude: number;
	countrySouthwestLongitude: number;
	countryNortheastLatitude: number;
	countryNortheastLongitude: number;
	countrySouthwestLatitude: number;
	citySouthwestLongitude: number;
	cityNortheastLatitude: number;
	cityNortheastLongitude: number;
	citySouthwestLatitude: number;
	officeSouthwestLongitude?: number;
	officeNortheastLatitude?: number;
	officeNortheastLongitude?: number;
	officeSouthwestLatitude?: number;
}

export interface ISelectedItem extends IMapPoint {
	avg: number | null;
	count?: number;
	title?: string;
	text?: string | JSX.Element;
	textOnly?: boolean;
	icon?: string;
}

export interface IPointWithOffset extends ISelectedItem {
	hasOfficeOffset?: boolean;
}

export interface IMarkerWithZIndex extends IPointWithOffset {
	zIndex?: number;
}

export interface IComment {
	id: number;
	comment: string;
	name: string;
	mark: number;
	serviceName: string;
	country: string;
	city: string;
	office: string;
}

export interface IRelatedComment extends IComment {
	role?: string;
}

export interface IResultComments extends IRelatedComment {
	email?: string;
}

export enum WordcloudTypes {
	negative = 'NEGATIVE',
	neutral = 'NEUTRAL',
	positive = 'POSITIVE',
}

export interface IWordcloudItem {
	text: string;
	value: number;
}

export interface ICommentsWordcloud
	extends Record<WordcloudTypes, IWordcloudItem[]> {}

export interface IBaseTrends {
	influencePriority: number;
	influence: number;
	processed: boolean;
	available: boolean;
	firstPerformanceResult: number;
	secondPerformanceResult: number;
	thirdPerformanceResult: number;
	examples: string[];
}

export interface ICurrentTrendData {
	satisfaction?: number;
	satisfactionType?: string;
	priority?: number;
	influence: number;
	focusName?: string;
	performanceResults: {
		firstPerformanceResult: number;
		secondPerformanceResult: number;
		thirdPerformanceResult: number;
	};
	serviceExamples: string[];
	influenceProcessed?: boolean;
	influenceAvailable?: boolean;
	submitted?: number;
	submittedHigh?: number;
}

export interface IFiltersToReset {
	[SurveyResultsFilterNames.region]: undefined;
	[SurveyResultsFilterNames.country]: undefined;
	[SurveyResultsFilterNames.city]: undefined;
	[SurveyResultsFilterNames.office]: undefined;
	[SurveyResultsFilterNames.businessUnit]: undefined;
	[SurveyResultsFilterNames.roleId]: undefined;
	statisticGroupingType: undefined;
}

export interface PageAndSizeParams {
	page: number;
	size: number;
}
